/**
 * useStaffManagement Hook
 * 직원 관리 상태 관리 및 비즈니스 로직
 */
import { useState, useCallback, useEffect } from 'react';
import type { Staff, StaffCreate, StaffRole } from '../models/StaffManagement';
import { staffManagementRepository } from '../repositories/StaffManagementRepository';
import { staffManagementService } from '../services/StaffManagementService';

/**
 * useStaffManagement 커스텀 훅
 */
export const useStaffManagement = () => {
  // 상태 관리
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<StaffRole | 'all'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [newStaff, setNewStaff] = useState<StaffCreate>({
    name: '',
    email: '',
    phone: '',
    role: 'staff',
  });

  /**
   * 직원 목록 로드
   */
  const loadStaffList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await staffManagementRepository.getStaffList();
      setStaffList(data);
    } catch (err) {
      console.error('직원 목록 로드 실패:', err);
      // API 실패 시 빈 배열 유지
      setStaffList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 초기 데이터 로드
   */
  useEffect(() => {
    loadStaffList();
  }, [loadStaffList]);

  /**
   * 필터링된 직원 목록
   */
  const filteredStaff = staffManagementService.applyFilters(
    staffList,
    searchTerm,
    selectedRole
  );

  /**
   * 직원 통계
   */
  const stats = staffManagementService.calculateStats(staffList);

  /**
   * 검색어 변경 핸들러
   */
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  /**
   * 역할 필터 변경 핸들러
   */
  const handleRoleFilterChange = useCallback((value: StaffRole | 'all') => {
    setSelectedRole(value);
  }, []);

  /**
   * 직원 생성 핸들러
   */
  const handleCreateStaff = useCallback(async () => {
    // 유효성 검사 — 이름 + 연락처 필수, 이메일은 선택
    if (!newStaff.name || !newStaff.phone) {
      alert('이름과 연락처는 필수입니다.');
      return;
    }

    // 이메일 입력했을 때만 형식 검증
    if (newStaff.email && !staffManagementService.isValidEmail(newStaff.email)) {
      alert('올바른 이메일 형식이 아닙니다.');
      return;
    }

    try {
      setLoading(true);
      await staffManagementRepository.createStaff(newStaff);
      await loadStaffList();
      setShowCreateModal(false);
      setNewStaff({ name: '', email: '', phone: '', role: 'staff' });
      alert(`${newStaff.name}님이 직원으로 등록되었습니다.`);
    } catch (err) {
      alert(err instanceof Error ? err.message : '직원 생성에 실패했습니다.');
      console.error('직원 생성 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [newStaff, loadStaffList]);

  /**
   * 직원 수정 핸들러
   */
  const handleUpdateStaff = useCallback(
    async (staffId: string, updateData: Partial<Staff>) => {
      try {
        setLoading(true);
        await staffManagementRepository.updateStaff(staffId, updateData);
        await loadStaffList();
        setShowEditModal(false);
        setSelectedStaff(null);
        alert('직원 정보가 수정되었습니다.');
      } catch (err) {
        alert(err instanceof Error ? err.message : '직원 수정에 실패했습니다.');
        console.error('직원 수정 실패:', err);
      } finally {
        setLoading(false);
      }
    },
    [loadStaffList]
  );

  /**
   * 계정 활성화/비활성화 핸들러
   */
  const handleToggleActive = useCallback(
    async (staff: Staff) => {
      const action = staff.isActive ? '비활성화' : '활성화';
      if (!window.confirm(`${staff.name}님의 계정을 ${action}하시겠습니까?`)) {
        return;
      }

      try {
        setLoading(true);
        await staffManagementRepository.toggleActive(staff.id, !staff.isActive);
        await loadStaffList();
        alert(`계정이 ${action}되었습니다.`);
      } catch (err) {
        alert(err instanceof Error ? err.message : `계정 ${action}에 실패했습니다.`);
        console.error('계정 상태 변경 실패:', err);
      } finally {
        setLoading(false);
      }
    },
    [loadStaffList]
  );

  /**
   * 직원 삭제 핸들러
   */
  const handleDeleteStaff = useCallback(
    async (staff: Staff) => {
      if (
        !window.confirm(
          `${staff.name}님의 계정을 완전히 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`
        )
      ) {
        return;
      }

      try {
        setLoading(true);
        await staffManagementRepository.deleteStaff(staff.id);
        await loadStaffList();
        alert('직원 계정이 삭제되었습니다.');
      } catch (err) {
        alert(err instanceof Error ? err.message : '직원 삭제에 실패했습니다.');
        console.error('직원 삭제 실패:', err);
      } finally {
        setLoading(false);
      }
    },
    [loadStaffList]
  );

  /**
   * 생성 모달 열기
   */
  const openCreateModal = useCallback(() => {
    setNewStaff({ name: '', email: '', phone: '', role: 'staff' });
    setShowCreateModal(true);
  }, []);

  /**
   * 생성 모달 닫기
   */
  const closeCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setNewStaff({ name: '', email: '', phone: '', role: 'staff' });
  }, []);

  /**
   * 수정 모달 열기
   */
  const openEditModal = useCallback((staff: Staff) => {
    setSelectedStaff(staff);
    setShowEditModal(true);
  }, []);

  /**
   * 수정 모달 닫기
   */
  const closeEditModal = useCallback(() => {
    setShowEditModal(false);
    setSelectedStaff(null);
  }, []);

  /**
   * 신규 직원 정보 업데이트
   */
  const updateNewStaff = useCallback((updates: Partial<StaffCreate>) => {
    setNewStaff((prev) => ({ ...prev, ...updates }));
  }, []);

  return {
    // 상태
    staffList,
    filteredStaff,
    loading,
    searchTerm,
    selectedRole,
    showCreateModal,
    showEditModal,
    selectedStaff,
    newStaff,
    stats,

    // 핸들러
    handleSearchChange,
    handleRoleFilterChange,
    handleCreateStaff,
    handleUpdateStaff,
    handleToggleActive,
    handleDeleteStaff,

    // 모달 제어
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,

    // 폼 제어
    updateNewStaff,

    // 데이터 로드
    loadStaffList,
  };
};
